<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useQuery } from "@tanstack/vue-query";

const props = defineProps({
  parentContainerRef: {
    type: [Object, null],
    required: true
  }
});

const stickyElement = ref(null);
const contentContainer = ref(null);
const componentIsFixed = ref(false);

onMounted(function() {
  window.addEventListener('scroll', handleScroll);
});

const { data: actionsProgress } = useQuery({
  queryKey: ['action-progress'],
  queryFn: async () => {
    const response = await window.axios.get("/ajax/v2/actions/progress");

    return response.data.data
  },
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

const handleScroll = () => {
  const containerRect = props.parentContainerRef.getBoundingClientRect();
  const stickyRect = stickyElement.value.getBoundingClientRect();
  const offsetTop = 65;

  componentIsFixed.value = containerRect.top <= offsetTop && stickyRect.top <= offsetTop;
}
</script>
<template>
  <!-- Stop snap on overlap when scrolling -->
  <div
    v-if="componentIsFixed"
    class="tw-h-52"
  />

  <div
    v-if="actionsProgress"
    ref="stickyElement"
    class="tw-bg-white tw-rounded-md tw-py-8 tw-w-full tw-top-[65px] tw-transition-transform tw-ease-in-out tw-duration-300 tw-z-[49]"
    :class="{ 'tw-scale-[1.10] tw-shadow-md tw-rounded-none tw-fixed tw-right-0 tw-left-0 tw-pb-6': componentIsFixed, 'tw-sticky': !componentIsFixed }"
  >
    <div
      ref="contentContainer"
      class="tw-px-8"
      :class="{ 'tw-m-auto tw-max-w-default-container tw-scale-[0.90]': componentIsFixed }"
    >
      <h4
        v-if="!componentIsFixed"
        class="tw-text-grey-dark tw-text-xl tw-pb-8"
      >
        You have completed <strong>{{ actionsProgress.actionPercentage }}%</strong> of
        your goal, <strong>{{ actionsProgress.periodPercentage }}%</strong> through the
        month.
      </h4>
      <div class="tw-mb-3 tw-relative tw-overflow-visible">
        <div
          class="tw-mt-6"
          aria-hidden="true"
        >
          <div class="tw-overflow-x-hidden tw-rounded-full tw-bg-grey-mid">
            <div
              class="tw-h-4 tw-rounded-full tw-bg-primary"
              :style="{ width: actionsProgress.actionPercentage + '%' }"
            />
            <div
              class="tw-absolute -tw-top-8"
              :style="{ left: actionsProgress.periodPercentage + '%' }"
            >
              <div
                class="tw-text-grey-dark tw-flex tw-flex-col tw-items-center tw-justify-center tw-absolute tw-left-[-19.5px]"
              >
                <span class="tw-text-sm tw-font-bold">Today</span>
                <svg-vue
                  class="tw-mb-[3px] tw-mt-1 tw-w-2 tw-h-[5px]"
                  icon="icons/caret-filled"
                />
                <div class="tw-h-4 tw-w-px tw-bg-[length:2px_6px] tw-bg-repeat-y tw-bg-[linear-gradient(to_bottom,theme(colors.grey.dark)_66.67%,transparent_33.33%)]" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="tw-flex tw-justify-between tw-text-sm tw-text-grey-dark"
        :class="{'tw-mb-8': !componentIsFixed}"
      >
        <strong>{{ actionsProgress.periodStart }}</strong>
        <strong>{{ actionsProgress.periodEnd }}</strong>
      </div>
    </div>
  </div>
</template>
